import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import FormRenderer from '../shared/FormRenderer';
import SuccessPopup from '../shared/SuccessPopup';
import AutoSaveToast from '../shared/AutoSaveToast';
import { confirmUngeocodedMapAddressIfNeeded } from '../../utils/mapSubmitGuard';
import { mapFieldHasPlacedMarker } from '../../utils/mapFieldValidation';
import { combineReturningStudentFormInstances, splitReturningStudentFormDataByInstance, extractReturningStudentInfo } from '../../utils/returningStudentFormCombiner';
import {
  normalizeHouseholdProfileData,
  resolveReturningInitialFieldValue
} from '../../utils/autofillProfileResolver';
import '../superadmin/AddDistrict.css';

const isEmptyAutofillTarget = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '');

const ReturningStudentEnrollmentForm = () => {
  const navigate = useNavigate();
  const { studentIntakeId } = useParams();
  const { user } = useAuth();
  const [studentIntake, setStudentIntake] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [combinedFormDefinition, setCombinedFormDefinition] = useState(null);
  const [formInstanceMap, setFormInstanceMap] = useState({});
  const [formInstances, setFormInstances] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [savingProgress, setSavingProgress] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [saveFeedbackModal, setSaveFeedbackModal] = useState(null);
  const [autoSaveToast, setAutoSaveToast] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [householdInformation, setHouseholdInformation] = useState({});
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  const [importedCSVData, setImportedCSVData] = useState(null);
  const [rolloverPrefillByFieldName, setRolloverPrefillByFieldName] = useState({});
  const previousFormValuesRef = useRef({});
  const deletedSiblingsRef = useRef(new Set());
  const studentBackfillAppliedForRef = useRef(null);

  // Get district name from user context
  const districtName = user?.district_name || '';

  // Load household information for autofill
  const loadHouseholdInformation = async () => {
    try {
      const response = await axios.get('/api/form-submissions/household-information');
      const householdData = normalizeHouseholdProfileData(response.data || {});
      setHouseholdInformation(householdData);
    } catch (error) {
      console.warn('Household information not available yet:', error);
      setHouseholdInformation({});
    }
  };

  const fetchStudentIntake = useCallback(async () => {
    try {
      const response = await axios.get(`/api/student-intake/my-intake/${studentIntakeId}`);
      setStudentIntake(response.data);
    } catch (error) {
      console.error('Failed to fetch student intake:', error);
      setError('Failed to load student information');
    }
  }, [studentIntakeId]);

  // Fetch siblings for autofill
  // Load imported CSV data for this student
  const loadImportedCSVData = useCallback(async () => {
    if (!studentIntakeId) return;
    
    try {
      const response = await axios.get(`/api/returning-student-imports/check/${studentIntakeId}`);
      if (response.data.hasImportedData) {
        setImportedCSVData(response.data.data);
      }
    } catch (error) {
      console.warn('No imported CSV data found for this student:', error);
    }
  }, [studentIntakeId]);

  const loadRolloverPrefill = useCallback(async () => {
    if (!studentIntakeId) return;
    try {
      const response = await axios.get(`/api/student-intake/my-intake/${studentIntakeId}/rollover-prefill`);
      const map = response.data?.prefillByFieldName;
      setRolloverPrefillByFieldName(map && typeof map === 'object' ? map : {});
    } catch (error) {
      console.warn('Rollover prefill not available:', error);
      setRolloverPrefillByFieldName({});
    }
  }, [studentIntakeId]);

  useEffect(() => {
    const loadSiblings = async () => {
      try {
        const response = await axios.get('/api/student-intake/my-intake');
        const allStudents = response.data;
        const siblingsList = allStudents.filter(student => student.id !== studentIntakeId);
        setSiblings(siblingsList);
      } catch (error) {
        console.error('Failed to fetch siblings:', error);
      }
    };

    if (studentIntakeId) {
      loadSiblings();
      fetchStudentIntake();
      loadHouseholdInformation();
      loadImportedCSVData();
      loadRolloverPrefill();
    } else {
      loadHouseholdInformation();
    }
  }, [studentIntakeId, fetchStudentIntake, loadImportedCSVData, loadRolloverPrefill]);

  // Fetch form instances
  const fetchFormInstances = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/form-instances/parent?enrollment_type=returning');
      const allInstances = response.data || [];

      // Filter for returning student form
      const returningStudentInstances = allInstances.filter(instance => {
        const formName = (instance.instance_name || instance.master_form_name || '').toLowerCase();
        return formName.includes('returning student');
      });

      if (returningStudentInstances.length === 0) {
        setError('No returning student form found. Please contact your district administrator.');
        setLoading(false);
        return;
      }

      setFormInstances(returningStudentInstances);

      // Combine form instances
      const combined = combineReturningStudentFormInstances(returningStudentInstances);
      if (combined) {
        setCombinedFormDefinition(combined);
        setFormInstanceMap(combined.formInstanceMap);
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch form instances:', error);
      setError('Failed to load forms');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFormInstances();
  }, [fetchFormInstances]);

  // Load saved progress
  const loadSavedProgress = useCallback(async () => {
    if (!studentIntakeId || !combinedFormDefinition) return;

    try {
      const progressResponse = await axios.get(`/api/form-submissions/progress?student_intake_id=${studentIntakeId}&form_type=returning_student`);
      const allFormData = progressResponse.data || [];
      
      const returningStudentProgress = allFormData.find(progress => 
        progress.form_type === 'returning_student' || 
        (progress.form_instance_id && formInstances.some(inst => inst.id === progress.form_instance_id))
      );

      if (returningStudentProgress && returningStudentProgress.submission_data) {
        const savedData = returningStudentProgress.submission_data;
        setFormValues(savedData);
        previousFormValuesRef.current = savedData;
        setHasLoadedProgress(true);
      }
    } catch (error) {
      console.error('Failed to load saved progress:', error);
    }
  }, [studentIntakeId, combinedFormDefinition, formInstances]);

  useEffect(() => {
    if (studentIntakeId && combinedFormDefinition) {
      loadSavedProgress();
    }
  }, [studentIntakeId, combinedFormDefinition, loadSavedProgress]);

  // One-time backfill pass per student/form render; fills only blank student fields.
  useEffect(() => {
    if (!studentIntakeId || !studentIntake || !combinedFormDefinition) return;
    if (Object.keys(formValues || {}).length === 0) return;
    const backfillSourceKey = [
      studentIntakeId,
      Object.keys(householdInformation || {}).length,
      Object.keys(rolloverPrefillByFieldName || {}).length,
      importedCSVData ? 'csv' : 'no_csv'
    ].join('|');
    if (studentBackfillAppliedForRef.current === backfillSourceKey) return;
    setFormValues((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((fieldKey) => {
        const parts = String(fieldKey || '').split('_');
        const originalFieldName = parts.length >= 2 ? parts.slice(1).join('_') : fieldKey;
        const resolvedValue = resolveReturningInitialFieldValue(originalFieldName, {
          studentIntake,
          householdInformation,
          rolloverPrefillByFieldName,
          importedCSVData
        });
        if (isEmptyAutofillTarget(next[fieldKey]) && resolvedValue !== undefined) {
          next[fieldKey] = resolvedValue;
          changed = true;
        }
      });
      if (!changed) return prev;
      previousFormValuesRef.current = next;
      return next;
    });
    studentBackfillAppliedForRef.current = backfillSourceKey;
  }, [studentIntakeId, studentIntake, combinedFormDefinition, householdInformation, rolloverPrefillByFieldName, importedCSVData, formValues]);

  // Initialize form values from student intake and household data
  useEffect(() => {
    if (!combinedFormDefinition || !formInstances.length) return;

    const initializedFormValues = { ...formValues };

    formInstances.forEach(instance => {
      const instanceId = instance.id;
      let formData = instance.form_data;
      
      if (typeof formData === 'string') {
        try {
          formData = JSON.parse(formData);
        } catch (e) {
          return;
        }
      }

      if (!formData || !formData.sections) return;

      formData.sections.forEach(section => {
        section.fields?.forEach(field => {
          const fieldKey = `${instanceId}_${field.name}`;
          
          const resolvedValue = resolveReturningInitialFieldValue(field.name, {
            studentIntake,
            householdInformation,
            rolloverPrefillByFieldName,
            importedCSVData
          });
          if (isEmptyAutofillTarget(initializedFormValues[fieldKey]) && resolvedValue !== undefined) {
            initializedFormValues[fieldKey] = resolvedValue;
          }
        });
      });
    });

    if (JSON.stringify(initializedFormValues) !== JSON.stringify(formValues)) {
      setFormValues(initializedFormValues);
      previousFormValuesRef.current = initializedFormValues;
    }
  }, [
    combinedFormDefinition,
    formInstances,
    studentIntake,
    householdInformation,
    rolloverPrefillByFieldName,
    importedCSVData,
    hasLoadedProgress,
    formValues
  ]);

  // Calculate form completion percentage
  const calculateProgress = useCallback(() => {
    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return 0;
    }

    let totalFields = 0;
    let filledFields = 0;

    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;

      section.fields?.forEach(field => {
        if (field.admin_only) return;

        // Check conditional visibility
        let isVisible = true;
        if (field.conditional_fields) {
          const conditionalValue = formValues[field.name];
          if (field.conditional_fields.hide_when === conditionalValue) {
            isVisible = false;
          } else if (field.conditional_fields.show_when && conditionalValue !== field.conditional_fields.show_when) {
            isVisible = false;
          }
        }

        if (!isVisible) return;

        totalFields++;
        const value = formValues[field.name];

        if (field.type === 'checkbox' || field.type === 'repeatable_group') {
          if (value && Array.isArray(value) && value.length > 0) {
            filledFields++;
          }
        } else if (value && (typeof value === 'string' && value.trim() !== '')) {
          filledFields++;
        }
      });
    });

    return totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  }, [combinedFormDefinition, formValues]);

  // Save progress
  const saveProgress = useCallback(async (options = {}) => {
    const trigger = options.trigger === 'manual' ? 'manual' : 'auto';
    if (savingProgress) return;

    const progressPercentage = calculateProgress();
    const hasUserInput = Object.values(formValues).some(
      (value) => value !== '' && (!Array.isArray(value) || value.length > 0)
    );

    if (!hasUserInput && progressPercentage === 0) {
      return;
    }

    try {
      setSavingProgress(true);

      await axios.post('/api/form-submissions/save-progress', {
        submission_data: formValues,
        progress_percentage: progressPercentage,
        form_type: 'returning_student',
        student_intake_id: studentIntakeId
      });

      setLastSavedTime(new Date());

      if (trigger === 'manual') {
        setSaveFeedbackModal({
          variant: 'success',
          title: 'Progress saved',
          message: 'Your form progress has been saved.'
        });
      } else {
        setAutoSaveToast({ variant: 'success', message: 'Progress saved automatically.' });
      }
    } catch (error) {
      console.error('Failed to save progress:', error);
      const msg =
        error.response?.data?.error || error.message || 'Could not save progress. Please try again.';
      if (trigger === 'manual') {
        setSaveFeedbackModal({
          variant: 'error',
          title: 'Save failed',
          message: msg
        });
      } else {
        setAutoSaveToast({ variant: 'error', message: 'Could not save progress automatically.' });
      }
    } finally {
      setSavingProgress(false);
    }
  }, [formValues, calculateProgress, studentIntakeId, savingProgress]);

  // Auto-save progress every 30 seconds
  useEffect(() => {
    if (Object.keys(formValues).length === 0) return;

    const autoSaveTimer = setTimeout(() => {
      saveProgress({ trigger: 'auto' });
    }, 30000);

    return () => clearTimeout(autoSaveTimer);
  }, [formValues, saveProgress]);

  // Handle form field changes
  const handleFormChange = (fieldName, value) => {
    const newValues = { ...formValues, [fieldName]: value };
    
    // Handle conditional field visibility and clearing
    if (combinedFormDefinition) {
      combinedFormDefinition.sections.forEach(section => {
        section.fields?.forEach(field => {
          if (field.conditional_fields) {
            const conditionalValue = newValues[field.name];
            
            // Hide and clear fields when condition is met
            if (field.conditional_fields.hide_when === conditionalValue) {
              field.conditional_fields.fields?.forEach(hiddenFieldName => {
                newValues[hiddenFieldName] = '';
              });
            }
          }

          // Handle autofill_source (mailing address same as current address)
          if (field.autofill_source && field.autofill_source.enabled) {
            if (field.name === fieldName && value === true) {
              // Checkbox checked - autofill target fields
              field.autofill_source.source_fields.forEach((sourceField, index) => {
                const targetField = field.autofill_source.target_fields[index];
                if (newValues[sourceField]) {
                  newValues[targetField] = newValues[sourceField];
                }
              });
            } else if (field.name === fieldName && value === false) {
              // Checkbox unchecked - clear target fields if disable_on_check is true
              if (field.autofill_source.disable_on_check) {
                field.autofill_source.target_fields.forEach(targetField => {
                  newValues[targetField] = '';
                });
              }
            }
          }
        });
      });
    }

    setFormValues(newValues);
    previousFormValuesRef.current = newValues;
    
    // Clear validation errors when user makes changes
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({});
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return errors;
    }

    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;

      section.fields?.forEach(field => {
        if (field.admin_only) return;

        // Check conditional visibility
        let isVisible = true;
        if (field.conditional_fields) {
          const conditionalValue = formValues[field.name];
          if (field.conditional_fields.hide_when === conditionalValue) {
            isVisible = false;
          } else if (field.conditional_fields.show_when && conditionalValue !== field.conditional_fields.show_when) {
            isVisible = false;
          }
        }

        // Only validate visible required fields
        if (!isVisible || !field.required) return;

        const value = formValues[field.name];

        if (field.type === 'checkbox') {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            errors[field.name] = `${field.label || field.name} is required`;
          }
        } else if (field.type === 'repeatable_group') {
          if (!value || !Array.isArray(value) || value.length === 0) {
            errors[field.name] = `At least one ${field.label || 'item'} is required`;
          }
        } else if (field.type === 'map') {
          if (!mapFieldHasPlacedMarker(value)) {
            errors[field.name] =
              `${field.label || field.name}: place a marker on the map (use Find on Map or click the map).`;
          }
        } else if (!value || (typeof value === 'string' && value.trim() === '')) {
          errors[field.name] = `${field.label || field.name} is required`;
        }

        if (field.type === 'phone') {
          const phoneValue = formValues[field.name];
          if (phoneValue && typeof phoneValue === 'string') {
            const digits = phoneValue.replace(/\D/g, '');
            if (digits.length > 0 && digits.length !== 10) {
              errors[field.name] = 'Please enter a valid phone number';
            }
          }
        }
      });
    });

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError('Please complete all required fields');
      
      const firstErrorField = Object.keys(errors)[0];
      const mapBtn = document.getElementById(`map-find-on-map-${firstErrorField}`);
      const fieldWrap = document.getElementById(`field-${firstErrorField}`);
      if (mapBtn || fieldWrap) {
        if (fieldWrap) fieldWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (mapBtn) setTimeout(() => mapBtn.focus(), 300);
      } else {
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }

      return;
    }

    if (combinedFormDefinition && !confirmUngeocodedMapAddressIfNeeded(combinedFormDefinition, formValues)) {
      return;
    }

    setLoading(true);

    try {
      // Extract student info from form data
      const studentInfo = extractReturningStudentInfo(formValues, formInstanceMap, formInstances);
      
      if (!studentInfo || !studentInfo.first_name || !studentInfo.last_name) {
        setError('Student name is required. Please fill in the student information section.');
        setLoading(false);
        return;
      }

      // Split form data by instance
      const splitData = splitReturningStudentFormDataByInstance(formValues, formInstanceMap, formInstances);
      
      // Prepare form_instances_data for submission
      const formInstancesData = {};

      formInstances.forEach(instance => {
        const instanceData = splitData[instance.id];

        if (instanceData) {
          formInstancesData[instance.id] = {
            student_name: `${studentInfo.first_name} ${studentInfo.last_name}`,
            data: instanceData,
            allows_multiple: instance.form_data?.allows_multiple || false
          };
        }
      });

      // Submit to form-submissions endpoint
      const submitData = {
        submission_data: formValues,
        form_instances_data: formInstancesData,
        student_info: studentInfo,
        form_type: 'returning_student'
      };

      if (studentIntakeId) {
        submitData.student_intake_id = studentIntakeId;
      }

      await axios.post('/api/form-submissions/submit', submitData, { timeout: 30000 });

      // Mark as submitted
      if (studentIntakeId) {
        sessionStorage.setItem(`returning_student_submitted_${studentIntakeId}`, 'true');
      }

      // Clear saved progress
      axios.post('/api/form-submissions/save-progress', {
        submission_data: {},
        progress_percentage: 0,
        student_intake_id: studentIntakeId,
        form_type: 'returning_student'
      }).catch(error => {
        console.error('Failed to clear saved progress:', error);
      });

      setFormValues({});
      setSuccess(true);
      setLoading(false);

      const studentName = `${studentInfo.first_name} ${studentInfo.last_name}`;
      setTimeout(() => {
        navigate(`/parent/student-intake?success=true&studentName=${encodeURIComponent(studentName)}`);
      }, 100);
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error.response?.data?.error || 'Failed to submit form');
      setLoading(false);
    }
  };

  // Handle delete progress
  const handleDeleteProgress = async () => {
    try {
      const progressResponse = await axios.get(`/api/form-submissions/progress?student_intake_id=${studentIntakeId}&form_type=returning_student`);
      const allFormData = progressResponse.data || [];
      
      const returningStudentProgress = allFormData.find(progress => 
        progress.form_type === 'returning_student'
      );

      if (returningStudentProgress) {
        await axios.delete(`/api/form-submissions/progress/${returningStudentProgress.id}`);
      }

      setFormValues({});
      sessionStorage.removeItem(`returning_student_submitted_${studentIntakeId}`);
      window.dispatchEvent(new CustomEvent('progressUpdated'));
      setHasLoadedProgress(false);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Failed to delete progress:', error);
      setError('Failed to delete form progress');
      setShowDeleteConfirm(false);
    }
  };

  // Handle adding siblings from student list
  const handleAddSiblingsFromList = useCallback(() => {
    if (siblings.length === 0) {
      return;
    }

    const updatedValues = { ...formValues };
    let addedCount = 0;

    combinedFormDefinition?.sections?.forEach(section => {
      section.fields?.forEach(field => {
        if (field.type !== 'repeatable_group') return;
        if (!field.fields?.some(subField => subField.name.toLowerCase().includes('sibling'))) return;

        const fieldName = field.name;
        const existingSiblings = updatedValues[fieldName] || [];
        const existingNames = new Set(
          existingSiblings.map(s => `${s.sibling_name || ''}`.toLowerCase().trim())
        );

        siblings.forEach(sibling => {
          const siblingName = `${sibling.first_name} ${sibling.last_name}`.trim();
          const siblingKey = siblingName.toLowerCase();
          
          if (!existingNames.has(siblingKey) && !deletedSiblingsRef.current.has(siblingKey)) {
            const siblingData = {
              sibling_name: siblingName,
              sibling_grade: '',
              sibling_building: ''
            };
            
            existingSiblings.push(siblingData);
            existingNames.add(siblingKey);
            addedCount++;
          }
        });

        updatedValues[fieldName] = existingSiblings;
      });
    });

    if (addedCount > 0) {
      setFormValues(updatedValues);
    }
  }, [siblings, formValues, combinedFormDefinition]);

  if (loading && !combinedFormDefinition) {
    return (
      <div className="add-district">
        <h2>Returning Student Enrollment{studentIntake ? ` - ${studentIntake.first_name}` : ''}</h2>
        <div>Loading forms...</div>
      </div>
    );
  }

  if (!combinedFormDefinition) {
    return (
      <div className="add-district">
        <h2>Returning Student Enrollment{studentIntake ? ` - ${studentIntake.first_name}` : ''}</h2>
        {error ? (
          <div className="error-message">{error}</div>
        ) : (
          <div>No forms available</div>
        )}
      </div>
    );
  }

  // Update form title with district name and transform conditional_fields to conditional structure
  const updatedFormDefinition = JSON.parse(JSON.stringify(combinedFormDefinition)); // Deep clone
  
  if (updatedFormDefinition.sections) {
    updatedFormDefinition.sections.forEach(section => {
      // Update district name in form header
      if (districtName && section.is_form_header && section.section_name) {
        section.section_name = section.section_name.replace('{District Name}', districtName);
      }
      
      // Transform conditional_fields structure to conditional structure for FormRenderer
      // The field with conditional_fields is the controlling field
      // conditional_fields.fields lists the fields that should be shown/hidden
      section.fields?.forEach(controllingField => {
        if (controllingField.conditional_fields && controllingField.conditional_fields.fields) {
          const controlledFieldNames = controllingField.conditional_fields.fields;
          const showWhen = controllingField.conditional_fields.show_when;
          const hideWhen = controllingField.conditional_fields.hide_when;
          
          // Find each controlled field and add conditional logic
          section.fields?.forEach(field => {
            if (controlledFieldNames.includes(field.name)) {
              if (showWhen) {
                field.conditional = {
                  field: controllingField.name,
                  value: [showWhen]
                };
              } else if (hideWhen) {
                // For hide_when, show when value is NOT hide_when
                const allValues = controllingField.options || ['Yes', 'No'];
                const showValues = allValues.filter(v => v !== hideWhen);
                field.conditional = {
                  field: controllingField.name,
                  value: showValues
                };
              }
              
              // Handle require_when_shown
              if (controllingField.conditional_fields.require_when_shown) {
                field.conditional_required = true;
              }
            }
          });
        }
      });
    });
  }

  return (
    <div className="add-district">
      <h2>Returning Student Enrollment{studentIntake ? ` - ${studentIntake.first_name} ${studentIntake.last_name}` : ''}</h2>

      {/* Progress Bar */}
      <div style={{
        background: '#ff9800',
        color: 'white',
        padding: '0.75rem 1.5rem',
        borderRadius: '4px',
        marginBottom: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <strong>Form Progress:</strong> {calculateProgress()}% complete
          {lastSavedTime && (
            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', opacity: 0.8 }}>
              Last saved: {lastSavedTime.toLocaleTimeString()}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => saveProgress({ trigger: 'manual' })}
          disabled={savingProgress}
          style={{
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: '1px solid white',
            padding: '0.4rem 0.8rem',
            borderRadius: '4px',
            cursor: savingProgress ? 'not-allowed' : 'pointer'
          }}
        >
          {savingProgress ? 'Saving...' : 'Save Progress'}
        </button>
      </div>

      <SuccessPopup
        show={!!saveFeedbackModal}
        onClose={() => setSaveFeedbackModal(null)}
        variant={saveFeedbackModal?.variant || 'success'}
        title={saveFeedbackModal?.title}
        message={saveFeedbackModal?.message || ''}
        autoCloseDelay={saveFeedbackModal?.variant === 'error' ? 0 : 5000}
      />
      <AutoSaveToast
        open={!!autoSaveToast}
        onClose={() => setAutoSaveToast(null)}
        variant={autoSaveToast?.variant || 'success'}
        message={autoSaveToast?.message || ''}
        duration={5000}
      />
      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="success-message" style={{ marginBottom: '1rem' }}>
          Form submitted successfully!
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <FormRenderer
          formData={updatedFormDefinition}
          formValues={formValues}
          onChange={handleFormChange}
          errors={validationErrors}
          userRole="parent"
          hideFormYear
          onAddSiblingsFromList={siblings.length > 0 ? handleAddSiblingsFromList : null}
        />
        
        <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={async () => {
                await saveProgress({ trigger: 'manual' });
                navigate('/parent/student-intake');
              }}
            >
              Save for Later
            </button>
          </div>
          {hasLoadedProgress && (
            <button 
              type="button" 
              className="btn-danger" 
              onClick={() => setShowDeleteConfirm(true)}
              style={{ background: '#dc3545', color: 'white', border: 'none' }}
            >
              Delete Progress
            </button>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Form Progress</h3>
              <p>Are you sure you want to delete your saved progress? This action cannot be undone.</p>
              <div className="modal-actions">
                <button onClick={handleDeleteProgress} className="btn-danger">Delete</button>
                <button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ReturningStudentEnrollmentForm;
